import { lazy, useContext, useState } from "react";
import { LicenseInfo } from "@mui/x-license-pro";
import PropTypes from "prop-types";
import { Navigate, Route, Routes } from "react-router-dom";
import DarkMode from "./components/system/darkMode/DarkMode";
import FirebaseNotification from "./components/comms/FirebaseNotification/FireBaseNotification";
import MyDivider from "./components/layout/MyDivider";
import RefLink from "./pages/frontPage/RefLink";
import LazyLoadTxt from "./components/layout/Progress/LazyLoadTxt";
import SystemError from "./components/system/SystemError/SystemError";
import FooterBar from "./components/layout/FooterBar/FooterBar";
import { userContext } from "./context/userContext";
import { DarkModeProvider } from "./context/DarkModeContext";
import LazyLoader from "./components/layout/Progress/LazyLoader";
import DotBoxLoader from "./components/layout/Progress/DotBoxLoader";
import { FPTilauksetProvider } from "./context/FPTilauksetContext";
import Empty from "./pages/Empty/Empty";
import AdminLayout from "./pages/admin/AdminLayout";
import FrontPageRedirector from "./pages/frontPage/components/FrontPageRedirector";
import { StatCacheProvider } from "./context/StatCacheContext";
import FrontPageAuth from "./pages/frontPage/FrontPageAuth";
import SiteAppBarLayout from "./components/layout/siteAppBar/SiteAppBarLayout";
import { GridProvider } from "./context/GridContext/GridContext";

import LomaCore from "./pages/loma/LomaCore";
import AiCore from "./pages/ai/AiCore";
import StatCore from "./pages/stat/StatCore";
import GridCore from "./pages/Grid/GridCore";
import AttachmentsCore from "./pages/attachments/AttachmentsCore";
import StepsCore from "./pages/steps/StepsCore";
import KotiCore from "./pages/koti/KotiCore";
import AddrAsiakasCore from "./pages/addr/AddrAsiakasCore";
import AddrTyomaaCore from "./pages/addr/AddrTyomaaCore";
import AddrPersonCore from "./pages/addr/AddrPersonCore";
import AddrTarjous from "./pages/addr/AddrTarjous";

const LandingHinnasto = lazy(() => import("./pages/landing/hinnasto/LandingHinnasto"));
const ConfirmCode = lazy(() => import("./pages/confirmCode/ConfirmCode"));
const FirebaseLogin = lazy(() => import("./pages/login/loginDialog/FirebaseLogin"));
const FirebaseLogout = lazy(() => import("./pages/login/FirebaseLogout"));
const FirebaseRegister = lazy(() => import("./pages/login/registerDialog/FirebaseRegister"));
const FirebaseReset = lazy(() => import("./pages/login/resetPasswordDialog/FirebaseReset"));

const HakuCore = lazy(() => import("./pages/haku/HakuCore"));
const LogsCore = lazy(() => import("./pages/admin/logs/LogsCore"));
const TyotCore = lazy(() => import("./pages/tyot/TyotCore"));
const GptPromptFP = lazy(() => import("./pages/GPT/GptPromptFP"));
const ResetPassword = lazy(() => import("./pages/login/resetPasswordDialog/ResetPassword"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const Keikat = lazy(() => import("./pages/keikat/layout/Keikat"));
const GetPumiFi = lazy(() => import("./pages/pumi.fi/GetPumiFI"));
const Gdpr = lazy(() => import("./pages/gdpr/Gdpr"));
const Support = lazy(() => import("./pages/support/Support"));

const BetoniTarjousAdmin = lazy(() => import("./pages/betoniTarjousAdmin/BetoniTarjousAdmin"));

// preloading lazy loaded components

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE);

function App() {
  // eslint-disable-next-line no-unused-vars
  const [systemError, setSystemError] = useState({ errorTxt: "", errorNum: 0 });
  const { isLoading, isSocketConnected, isLoggedIn, user } = useContext(userContext) || {};
  function RequireAuth({ children }) {
    return isLoggedIn ? children : <Navigate to="/" />;
  }
  RequireAuth.propTypes = {
    children: PropTypes.element,
  };
  function ShowDotBoxLoaders() {
    return (
      <LazyLoadTxt
        obj={
          <div style={{ opacity: "1" }}>
            <DotBoxLoader />
          </div>
        }
      />
    );
  }

  return (
    <DarkModeProvider person={user}>
      <SystemError
        errorTxt={systemError && systemError.errorTxt}
        errorNum={systemError && systemError.errorNum}
      />
      <DarkMode>
        <LazyLoader>
          <StatCacheProvider>
            <GridProvider>
              {isLoading && !user ? (
                <ShowDotBoxLoaders />
              ) : (
                <>
                  {isLoggedIn ? (
                    <>
                      <div className="appWrapper">
                        <SiteAppBarLayout />
                        <Routes>
                          <Route exact path="/" element={<FrontPageRedirector />} />
                          {CommonRoutes()}
                          {AuthenticatedRoutes()}
                        </Routes>
                        <MyDivider gutter gutterHeight="3px" />
                      </div>
                      <FooterBar noInfo />
                      <FirebaseNotification />
                    </>
                  ) : (
                    <div>
                      <SiteAppBarLayout />
                      <div
                        style={{
                          minHeight: "calc(100vh - 134px)",
                          height: "100%",
                        }}>
                        <Routes>
                          <Route exact path="/" element={<GptPromptFP />} />
                          {CommonRoutes()}
                        </Routes>
                      </div>
                      <FooterBar noInfo />
                    </div>
                  )}
                  <RefLink />
                </>
              )}
            </GridProvider>
          </StatCacheProvider>
        </LazyLoader>
      </DarkMode>
    </DarkModeProvider>
  );

  function AuthenticatedRoutes() {
    return (
      <>
        <Route
          exact
          path="/list"
          element={
            <FPTilauksetProvider>
              <FrontPageAuth />
            </FPTilauksetProvider>
          }
        />
        <Route exact path="/koti" element={<KotiCore />} />
        <Route exact path="/tyot" element={<TyotCore />} />
        <Route exact path="/haku" element={<HakuCore />} />
        <Route exact path="/search" element={<HakuCore />} />
        <Route exact path="/raport" element={<StatCore />} />
        <Route exact path="/report" element={<StatCore />} />
        <Route exact path="/liitteet" element={<AttachmentsCore />} />
        <Route exact path="/liite" element={<AttachmentsCore />} />
        <Route exact path="/attachments" element={<AttachmentsCore />} />
        <Route exact path="/grid" element={<GridCore />} />
        <Route exact path="/loma" element={<LomaCore />} />
        <Route exact path="/lomat" element={<LomaCore />} />
        <Route exact path="/ai" element={<AiCore />} />
        <Route exact path="/admin" element={<AdminLayout />} />
        <Route exact path="/logs" element={<LogsCore />} />
        {/* EDITORS */}
        <Route exact path="/asiakas/:asiakasId" element={<AddrAsiakasCore />} />
        <Route exact path="/asiakas/" element={<AddrAsiakasCore />} />
        <Route exact path="/tyomaa/:tyomaaId" element={<AddrTyomaaCore keikkaId={null} />} />
        <Route exact path="/tyomaa/" element={<AddrTyomaaCore keikkaId={null} />} />
        <Route exact path="/person/:personId" element={<AddrPersonCore />} />
        <Route exact path="/person/" element={<AddrPersonCore />} />
        <Route exact path="/tarjous/:tarjousId" element={<AddrTarjous />} />
        <Route exact path="/tarjous/" element={<AddrTarjous />} />
        <Route exact path="/empty" element={<Empty />} />

        <Route exact path="/admin/hinta" element={<BetoniTarjousAdmin />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/tuki" element={<Support />} />
        <Route path="/keikat" element={<Keikat />} />
      </>
    );
  }

  function CommonRoutes() {
    return (
      <>
        <Route path="*" element={<Page404 />} />
        <Route
          exact
          path="/tilaus/:keikkaId"
          element={<StepsCore isConnected={isSocketConnected} />}
        />
        <Route
          exact
          path="/keikka/:keikkaId"
          element={<StepsCore isConnected={isSocketConnected} />}
        />
        <Route exact path="/cc/:confirmCode" element={<ConfirmCode />} />
        <Route exact path="/pumi.fi/:keikkaId_v2" element={<GetPumiFi />} />
        <Route exact path="/gdpr" element={<Gdpr />} />
        <Route exact path="/loggedIn" element={<Navigate to="/" />} />
        <Route exact path="/login" element={<FirebaseLogin />} />
        <Route exact path="/logout" element={<FirebaseLogout />} />
        <Route exact path="/register" element={<FirebaseRegister />} />
        <Route exact path="/reset" element={<FirebaseReset />} />
        <Route exact path="/resetPw/:token" element={<ResetPassword />} />

        {/* landing */}
        <Route exact path="/hinnasto" element={<LandingHinnasto />} />
      </>
    );
  }
}

export default App;

const Page404 = () => <Navigate to="/" />;
